import { Link } from "react-router-dom";
import Rating from "@mui/material/Rating";
import QuantityBox from "../../Components/QuantityBox";
import { IoIosClose } from "react-icons/io";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleWishlistItem,
  setWishlistItems,
  clearWishlist,
} from "../../store/slices/WishlistSlice";
import { addItemToCart } from "../../store/slices/CartSlice";

import emprtCart from "../../assets/images/myList.png";
import { MyContext } from "../../App";
import { useContext, useEffect, useState, useMemo } from "react";
import { deleteData, fetchDataFromApi } from "../../utils/api";
import { FaHome } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import TablePagination from "@mui/material/TablePagination";

const MyList = () => {
  const { wishlistItems: wishlist, loading: wishlistLoading } = useSelector(
    (state) => state.wishlist
  );
  const { cartItems, loading: cartLoading } = useSelector(
    (state) => state.cart
  );
  const [isLoading, setIsLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const context = useContext(MyContext);
  const dispatch = useDispatch();
  const history = useNavigate();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [page, setPage] = useState(0); // TablePagination is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cartProductIds = useMemo(
    () => cartItems.map((item) => item.productId?._id),
    [cartItems]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem("token");

    if (!token) {
      history("/");
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please Login to see your wishlist",
      });
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    setIsLoading(true);
    fetchDataFromApi(
      `/api/my-list?userId=${user?._id}&page=${page + 1}&perPage=${rowsPerPage}`
    )
      .then((res) => {
        dispatch(setWishlistItems(res.data || []));
        if (res.pagination) setPagination(res.pagination);
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          localStorage.clear();
          history("/");
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Session expired. Please login again.",
          });
        } else {
          context.setAlertBox({
            open: true,
            error: true,
            msg: "Failed to load wish list. Please try again.",
          });
        }
      })
      .finally(() => setIsLoading(false));
    context.setEnableFilterTab(false);
  }, [page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const removeItem = async (productId) => {
    setIsLoading(true);
    try {
      await dispatch(toggleWishlistItem(productId)).unwrap();
      context.setAlertBox({
        open: true,
        error: false,
        msg: "Item removed from My List!",
      });
    } catch (error) {
      console.error("Error removing item from My List:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to remove item from My List",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addToCartHandler = async (product) => {
    if (!product || !product.prices || product.prices.length === 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "This product has no price information and cannot be added to the cart.",
      });
      return;
    }

    setAddingId(product._id);

    const data = {
      priceId: product.prices[0]._id,
      quantity: 1,
      productId: product._id,
    };

    try {
      await dispatch(addItemToCart(data)).unwrap();
      context.setAlertBox({
        open: true,
        error: false,
        msg: "Item added to cart successfully!",
      });
    } catch (error) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: error || "Failed to add item to cart.",
      });
    } finally {
      setAddingId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>My List - Sabba Farm</title>
        <meta
          name="description"
          content="View your saved products on Sabba Farm - India's No.1 Agri Shop for seeds, fertilizers, pesticides, and tools."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://sabbafarm.com/my-list" />
        <meta property="og:title" content="My Saved Products | Sabba Farm" />
        <meta
          property="og:description"
          content="Check out the products you've saved for later purchase at Sabba Farm – Trusted agri marketplace."
        />
        <meta property="og:url" content="https://sabbafarm.com/my-list" />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="section cartPage">
        <div className="container">
          {wishlistLoading ? (
            <div className="flex items-center justify-center w-full min-h-[300px]">
              <CircularProgress />
            </div>
          ) : (
            <div className="myListTableWrapper px-4 max-md:px-2">
              <h2 className="hd mb-1">My List</h2>
              <p className="mb-4">
                There are <b className="text-red">{wishlist.length}</b> products
                in your My List
              </p>
              {wishlist.length !== 0 ? (
                <div className="row">
                  <div className="col-md-12">
                    <div className="table-responsive myListTable">
                      <table className="table">
                        <thead>
                          <tr>
                            <th className="w-[35%] md:w-[50%]">Product</th>
                            <th className="w-[15%] md:w-[25%]">Unit Price</th>
                            <th className="w-[10%] md:w-[10%]">Remove</th>
                            <th className="w-[20%] md:w-[15%]">Add to Cart</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wishlist.map((item) => {
                            const product = item.productId || item;
                            if (!product) return null;

                            const isAddedToCart = cartProductIds.includes(
                              product._id
                            );
                            const isOutOfStock =
                              product.prices?.[0]?.countInStock < 1;

                            return (
                              <tr key={item._id}>
                                <td className="w-[35%] md:w-[50%]">
                                  <Link to={`/product/${product.id}`}>
                                    <div className="d-flex align-items-center cartItemimgWrapper">
                                      <div className="imgWrapper">
                                        <img
                                          src={product?.images?.[0]}
                                          className="w-100"
                                          alt={product?.name}
                                        />
                                      </div>
                                      <div className="info px-3">
                                        <h6 className="text-sm md:text-base">
                                          {product?.name}
                                        </h6>
                                        {product?.rating && (
                                          <Rating
                                            name="read-only"
                                            value={product?.rating}
                                            readOnly
                                            size="small"
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </Link>
                                </td>
                                <td className="w-[15%] md:w-[15%] text-sm md:text-base">
                                  Rs {product?.prices?.[0]?.actualPrice}
                                </td>
                                <td className="w-[10%] md:w-[10%]">
                                  <span
                                    className="remove"
                                    onClick={() => removeItem(product._id)}
                                  >
                                    <IoIosClose />
                                  </span>
                                </td>
                                <td className="w-[40%] md:w-[25%]">
                                  <button
                                    onClick={() => addToCartHandler(product)}
                                    className={`bg-green-700 text-white py-2 px-4 rounded whitespace-nowrap hover:bg-green-800 active:bg-green-900 transform hover:scale-105 active:scale-95 transition duration-300 ${
                                      isAddedToCart ||
                                      isOutOfStock ||
                                      (cartLoading && addingId === product._id)
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    disabled={
                                      isAddedToCart ||
                                      isOutOfStock ||
                                      (cartLoading && addingId === product._id)
                                    }
                                  >
                                    {cartLoading && addingId === product._id
                                      ? "Adding..."
                                      : isAddedToCart
                                      ? "Added"
                                      : "Add to Cart"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="emptyCart d-flex align-items-center justify-content-center flex-column">
                  <img src={emprtCart} alt="empty list" />
                  <h3>Your list is empty</h3>
                  <p>Please add something to your list</p>
                  <Link to="/">
                    <Button className="btn-g">
                      <FaHome /> Go To Home
                    </Button>
                  </Link>
                </div>
              )}
              <TablePagination
                component="div"
                count={pagination.totalItems || 0}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                sx={{
                  "& .MuiTablePagination-actions .MuiIconButton-root": {
                    color: "black",
                  },
                }}
              />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default MyList;
